/**
 * @quotech/ui - Shared Formatting Utilities
 *
 * Canonical implementations of formatting functions used across ALL hubs.
 * Every hub should import these from '@quotech/ui' instead of defining
 * local copies.
 *
 * Locale: en-AU throughout (Australian English).
 * Timezone: Australia/Sydney — all dates are pinned to AEST/AEDT so they
 * display consistently regardless of the viewer's browser timezone.
 */

const QUOTECH_TZ = 'Australia/Sydney'

// ---------------------------------------------------------------------------
// Currency
// ---------------------------------------------------------------------------

/**
 * Format a number as currency (AUD by default).
 *
 * Accepts strings too — Django DecimalField serializes to JSON
 * strings to preserve precision (`"123.45"` not `123.45`). When
 * a string is passed we coerce via `Number()`; non-numeric input
 * falls back to `$0.00` rather than `$NaN`.
 *
 * @example formatCurrency(1234.5)         // "$1,234.50"
 * @example formatCurrency('1234.5')       // "$1,234.50"
 * @example formatCurrency(1234.5, 'USD')  // "US$1,234.50"
 */
export function formatCurrency(
  amount: number | string | null | undefined,
  currency = 'AUD',
): string {
  if (amount === null || amount === undefined || amount === '') return '$0.00'
  const numeric = typeof amount === 'number' ? amount : Number(amount)
  if (!Number.isFinite(numeric)) return '$0.00'
  return new Intl.NumberFormat('en-AU', {
    style: 'currency',
    currency,
  }).format(numeric)
}

/**
 * Compact currency — uses K / M suffixes for large values.
 *
 * @example formatCurrencyCompact(1_200_000) // "$1.2M"
 * @example formatCurrencyCompact(45_000)    // "$45.0K"
 */
export function formatCurrencyCompact(
  amount: number | null | undefined,
): string {
  if (amount === null || amount === undefined) return '$0'
  const abs = Math.abs(amount)
  const sign = amount < 0 ? '-' : ''
  if (abs >= 1_000_000_000) return `${sign}$${(abs / 1_000_000_000).toFixed(1)}B`
  if (abs >= 1_000_000) return `${sign}$${(abs / 1_000_000).toFixed(1)}M`
  if (abs >= 1_000) return `${sign}$${(abs / 1_000).toFixed(1)}K`
  return `${sign}$${abs.toFixed(0)}`
}

// ---------------------------------------------------------------------------
// Numbers
// ---------------------------------------------------------------------------

/**
 * Format a number with locale-aware grouping.
 *
 * @example formatNumber(1234567) // "1,234,567"
 */
export function formatNumber(value: number | null | undefined): string {
  if (value === null || value === undefined) return '0'
  return new Intl.NumberFormat('en-AU').format(value)
}

/**
 * Format a count for a small header / dock badge.
 *
 * Caps at 99+ (same as the notification bell). Never use a 9+ cap: a fixed
 * 14px pill cannot fit "9+", and capping at nine just lies once anything
 * interesting piles up. Pair with a `min-w` badge (not a fixed `w-3.5` /
 * `w-4`) so two-digit counts and "99+" still fit.
 *
 * @example formatCountBadge(3)    // "3"
 * @example formatCountBadge(42)   // "42"
 * @example formatCountBadge(100)  // "99+"
 * @example formatCountBadge(0)    // "0" — callers still decide whether to
 *                                 // hide the badge when the count is zero
 */
export function formatCountBadge(
  value: number | null | undefined,
  cap = 99,
): string {
  const n = typeof value === 'number' && Number.isFinite(value) ? value : 0
  const safeCap = Number.isFinite(cap) && cap > 0 ? Math.floor(cap) : 99
  if (n > safeCap) return `${safeCap}+`
  return String(Math.max(0, Math.floor(n)))
}

/**
 * Format a percentage value.
 *
 * @example formatPercent(42.567) // "42.6%"
 */
export function formatPercent(
  value: number | null | undefined,
  decimals = 1,
): string {
  if (value === null || value === undefined) return '0%'
  return `${value.toFixed(decimals)}%`
}

// ---------------------------------------------------------------------------
// Dates & Times
// ---------------------------------------------------------------------------

/**
 * Format a date string to "DD Mon YYYY" (en-AU).
 *
 * @example formatDate('2025-03-15') // "15 Mar 2025"
 */
export function formatDate(
  date: string | Date | null | undefined,
): string {
  if (!date) return '\u2014' // em dash
  try {
    const d = typeof date === 'string' ? new Date(date) : date
    if (isNaN(d.getTime())) return '\u2014'
    return d.toLocaleDateString('en-AU', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      timeZone: QUOTECH_TZ,
    })
  } catch {
    return '\u2014'
  }
}

/**
 * Format a date and time string.
 *
 * @example formatDateTime('2025-03-15T14:30:00Z') // "15 Mar 2025, 02:30 pm"
 */
export function formatDateTime(
  date: string | Date | null | undefined,
): string {
  if (!date) return '\u2014'
  try {
    const d = typeof date === 'string' ? new Date(date) : date
    if (isNaN(d.getTime())) return '\u2014'
    return d.toLocaleString('en-AU', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      timeZone: QUOTECH_TZ,
    })
  } catch {
    return '\u2014'
  }
}

/**
 * Format a date as relative time ("2h ago", "3d ago", etc.).
 * Falls back to `formatDate` for dates older than 7 days.
 */
export function formatRelativeTime(
  date: string | Date | null | undefined,
): string {
  if (!date) return 'Never'
  try {
    const d = typeof date === 'string' ? new Date(date) : date
    const now = new Date()
    const diffMs = now.getTime() - d.getTime()
    const diffMins = Math.floor(diffMs / 60_000)
    const diffHours = Math.floor(diffMins / 60)
    const diffDays = Math.floor(diffHours / 24)

    if (diffMins < 1) return 'Just now'
    if (diffMins < 60) return `${diffMins}m ago`
    if (diffHours < 24) return `${diffHours}h ago`
    if (diffDays < 7) return `${diffDays}d ago`

    return formatDate(date)
  } catch {
    return '\u2014'
  }
}

// ---------------------------------------------------------------------------
// Bytes
// ---------------------------------------------------------------------------

/**
 * Format a byte count as human-readable (KB, MB, GB, ...).
 *
 * @example formatBytes(1_048_576) // "1.00 MB"
 */
export function formatBytes(bytes: number, decimals = 2): string {
  if (bytes === 0) return '0 B'
  const k = 1024
  const dm = decimals < 0 ? 0 : decimals
  const sizes = ['B', 'KB', 'MB', 'GB', 'TB', 'PB']
  const i = Math.floor(Math.log(bytes) / Math.log(k))
  return `${parseFloat((bytes / Math.pow(k, i)).toFixed(dm))} ${sizes[i]}`
}

// ---------------------------------------------------------------------------
// Hours
// ---------------------------------------------------------------------------

/**
 * Format decimal hours as "Xh Ym".
 *
 * @example formatHours(2.5) // "2h 30m"
 */
export function formatHours(hours: number): string {
  const h = Math.floor(hours)
  const m = Math.round((hours - h) * 60)
  if (m === 0) return `${h}h`
  return `${h}h ${m}m`
}
